import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, mergeProps as _mergeProps, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withKeys as _withKeys, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = {
    class: "px-5 pt-6 pb-4 flex flex-col flex-1 min-h-0",
    "data-testid": "donation-list-view"
};
const _hoisted_2 = { class: "flex items-center justify-between mb-6 sm:mb-8" };
const _hoisted_3 = {
    class: "text-2xl sm:text-3xl font-bold text-gray-900 shrink-0",
    "data-testid": "donation-list-title"
};
import { ref, computed, watch, h, onMounted } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { Avatar, Badge, Button, DataTable, Modal, Pager, TableFilterSelector, TableShell, TextInput, useTableQuery, useTableViews, } from "@eudonet/opal";
import { useDonationStore } from "../../stores/useDonationStore";
import { useDonationOptions } from "../composables/useDonationOptions";
import { useCampaignOptions } from "../composables/useCampaignOptions";
import { useDonationViewPreferences } from "../composables/useDonationViewPreferences";
import { DONATION_VIEW_PRESETS } from "../composables/donationViewPresets";
import { DONATION_SOURCE_NONE, serializeDonationSourceFilter, deserializeDonationSourceFilter, deserializeCampaignIdFilter, hasUrlListSelection, mapFiltersToSearchParams, } from "../composables/donationListFilterParams";
import { useFinanceFormatters } from "@/shared/composables/useFinanceFormatters";
import { useAppToast } from "@/shared/composables/useAppToast";
import { useTableQueryUrlSync } from "@/shared/composables/useTableQueryUrlSync";
import { useListSettings } from "@/shared/composables/useListSettings";
import { handleNavigationFailure } from "@/shared/utils/navigation";
import { buildTableLabels } from "@/shared/composables/tableLabels";
import { donationSearchParts } from "../composables/tableSearch";
import { matchesSearchTerm } from "@/shared/utils/tableSearch";
import { watchDebounced } from "@vueuse/core";
import { donationStatusTheme, donationStatusIcon } from "../composables/donationStatusBadge";
import DonationCreateModal from "../components/DonationCreateModal.vue";
import { buildFilterSelectorLabels } from "@/shared/composables/filterSelectorLabels";
import { useListRowSelection } from "@/shared/composables/useListRowSelection";
import ListFloatActionBar from "@/shared/components/ListFloatActionBar.vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'DonationListView',
    setup(__props) {
        const { t } = useI18n();
        const filterSelectorLabels = computed(() => buildFilterSelectorLabels(t));
        const { formatAmount, formatDate } = useFinanceFormatters();
        const { toastError } = useAppToast();
        const store = useDonationStore();
        const route = useRoute();
        const router = useRouter();
        const showCreateModal = ref(route.query.action === "new");
        const createModalMounted = ref(showCreateModal.value);
        watch(showCreateModal, (open) => {
            if (open)
                createModalMounted.value = true;
        });
        const ROW_OPTIONS = [10, 30, 50];
        // ── Filter option catalogs (status / type / payment method) ───────────────────
        // Selectable values for the "Filtrer" dropdown and the clickable filter pills.
        // Sourced from useDonationOptions() — the catalog-backed single source of truth
        // (BFF GET /donations/catalogs, cached idempotently in the store and shared with
        // the create modal). Deriving from the catalog rather than a hardcoded list keeps
        // the filter in sync with the backend: a status/type/method added (or removed)
        // server-side flows through here with no page edit. The selector maps value→id.
        const { statusOptions, donationTypeOptions, paymentMethodOptions, donationSourceOptions } = useDonationOptions();
        const toFilterItems = (options) => options.map((o) => ({ id: o.value, label: o.label }));
        const statusItems = computed(() => toFilterItems(statusOptions.value));
        const donationTypeItems = computed(() => toFilterItems(donationTypeOptions.value));
        const paymentMethodItems = computed(() => toFilterItems(paymentMethodOptions.value));
        const donationSourceItems = computed(() => [
            { id: DONATION_SOURCE_NONE, label: t("finance.donations.filter.noSource") },
            ...toFilterItems(donationSourceOptions.value),
        ]);
        // The donations list filters by ANY campaign status (a completed/archived campaign's past
        // donations must stay reachable) — unlike the donation-detail attach picker (Active-only default).
        const campaignOptions = useCampaignOptions({ status: null });
        const campaignItems = computed(() => campaignOptions.options.value.map((o) => ({ id: String(o.value), label: o.label })));
        // ── Table query (search, filters, sort, pagination) ──────────────────────────
        // filterLabels names each pill's dimension ("Statut"); filterItems resolves each value id
        // to its localized label so a pill reads "Statut : Validé" / "Statut (4)" instead of raw keys.
        const tableQuery = useTableQuery({
            pageSize: ROW_OPTIONS[0],
            // Getters rather than computed(): useTableQuery declares `filterLabels`/`filterItems` as
            // MaybeRefOrGetter<Record<…>>, so either form is accepted. The getters are reactive all the
            // same — they run inside useTableQuery's activeFilterPills computed, which tracks the locale
            // read by t(). (Nothing forces the choice; the brand mismatch this comment used to cite was a
            // duplicate-Vue artefact, gone with the single-Vue `paths` pin in tsconfig.json.)
            filterLabels: () => ({
                status: t("finance.donations.columns.status"),
                donationType: t("finance.donations.columns.type"),
                paymentMethod: t("finance.donations.columns.paymentMethod"),
                donationSource: t("finance.donations.filter.donationSource"),
                campaignId: t("finance.donations.columns.campaign"),
            }),
            filterItems: () => ({
                status: statusItems.value,
                donationType: donationTypeItems.value,
                paymentMethod: paymentMethodItems.value,
                donationSource: donationSourceItems.value,
                campaignId: campaignItems.value,
            }),
        });
        // ── Load donations from BFF ───────────────────────────────────────────────────
        // Column id → `sortBy`. Only columns whose sort both backends accept are sortable.
        const SORT_FIELD_BY_COLUMN = {
            amount: "totalAmount",
            donationDate: "donationDate",
        };
        async function loadDonations() {
            const sort = tableQuery.state.sort;
            const sortBy = sort ? SORT_FIELD_BY_COLUMN[sort.column] : undefined;
            await store.loadDonations({
                // Free text goes to the BFF only when OPSEARCH serves the list (OP-6445), see
                // DONATIONS_SEARCH_BACKEND_HEADER. On OPDON, whose `q` matches the label only, the term filters the
                // loaded page instead (see `filteredDonations`). Blank sends no `q`.
                q: store.searchedByServer ? tableQuery.state.search?.trim() || undefined : undefined,
                ...mapFiltersToSearchParams(tableQuery.state.filters),
                page: tableQuery.state.page,
                pageSize: tableQuery.state.pageSize,
                sortBy,
                sortOrder: sortBy ? sort?.direction : undefined,
                allowSearchIndex: true,
                tabId: tableQuery.state.activeViewId || undefined,
                skipIfCached: true,
            });
        }
        /**
         * `store.loadDonations` re-throws (e.g. a backend-rejected URL filter value). Both call sites
         * below need the same protection: an unhandled rejection here would otherwise abort whatever
         * `await`s it next — on mount, `restoreViewPreferences()` — leaving the saved "Mes listes" tabs
         * unrestored on top of an empty, unexplained table.
         */
        async function loadDonationsSafe() {
            try {
                await loadDonations();
            }
            catch {
                // User feedback via toast is enough for this catch (R46) — no logger/console needed (R47).
                toastError("finance.donations.loadError");
            }
        }
        onMounted(async () => {
            const urlCampaignId = tableQuery.state.filters["campaignId"];
            // wide: the filter's step-2 search is client-side over the loaded set, so the dropdown's default
            // 50-item page would silently hide any campaign beyond it (and a deep-linked one outside the page
            // would fall back to its raw GUID were it not for ensureSelectedId below).
            void campaignOptions.load({
                wide: true,
                ensureSelectedId: urlCampaignId?.type === "in" ? urlCampaignId.values[0] : undefined,
            });
            await loadDonationsSafe();
            // Restore the user's saved prefiltered lists + active tab from OPREF — must run even when the
            // initial load above failed.
            await restoreViewPreferences();
        });
        // ── Client-side search (OPDON backend) ────────────────────────────────────────
        // While OPDON serves the list (BFF header `X-Donations-Search-Backend`, see the store's
        // `searchedByServer`), the term filters the donations currently loaded in the table: it matches the
        // visible cell content (donor, campaign, formatted amount/date and the translated status / type /
        // payment-method labels), with no request. Once OPSEARCH serves the list, the server does it.
        const clientSearchActive = computed(() => !store.searchedByServer && !!tableQuery.state.search?.trim());
        const filteredDonations = computed(() => {
            if (!clientSearchActive.value)
                return store.donations;
            const term = tableQuery.state.search ?? "";
            return store.donations.filter((d) => matchesSearchTerm(donationSearchParts(d, { t, formatAmount, formatDate }), term));
        });
        // A client-side search shows the filtered subset of the current page, so the pager reflects that
        // subset (one page, filtered count) instead of the server totals — otherwise it would claim
        // "X of 100 / page 1 of 10" while the table shows 2 rows. Otherwise the server pagination drives it.
        const pagerTotalItems = computed(() => clientSearchActive.value ? filteredDonations.value.length : store.totalItems);
        const pagerTotalPages = computed(() => (clientSearchActive.value ? 1 : store.totalPages));
        const pagerCurrentPage = computed(() => (clientSearchActive.value ? 1 : tableQuery.state.page));
        // ── Preset views (tab strip at Level 1) ──────────────────────────────────────
        const tableViews = useTableViews({
            initialViews: [
                {
                    id: "all",
                    label: t("finance.donations.title"),
                    isPreset: true,
                    isDefault: true,
                    querySnapshot: {},
                },
                {
                    id: "validated",
                    label: t("finance.donations.status.Validated"),
                    isPreset: true,
                    querySnapshot: { filters: { status: { type: "in", values: ["Validated"] } } },
                },
                {
                    id: "pending",
                    label: t("finance.donations.status.Pending"),
                    isPreset: true,
                    querySnapshot: { filters: { status: { type: "in", values: ["Recorded"] } } },
                },
                {
                    id: "recurring",
                    label: t("finance.donations.type.Regular"),
                    isPreset: true,
                    querySnapshot: { filters: { donationType: { type: "in", values: ["Regular"] } } },
                },
                {
                    id: "refunded",
                    label: t("finance.donations.status.Refunded"),
                    isPreset: true,
                    querySnapshot: { filters: { status: { type: "in", values: ["Refunded"] } } },
                },
                {
                    id: "cash",
                    label: t("finance.donations.paymentMethod.Cash"),
                    isPreset: true,
                    querySnapshot: { filters: { paymentMethod: { type: "in", values: ["Cash"] } } },
                },
                {
                    id: "cheque",
                    label: t("finance.donations.paymentMethod.Cheque"),
                    isPreset: true,
                    querySnapshot: { filters: { paymentMethod: { type: "in", values: ["Cheque"] } } },
                },
                {
                    id: "bank-transfer",
                    label: t("finance.donations.paymentMethod.BankTransfer"),
                    isPreset: true,
                    querySnapshot: { filters: { paymentMethod: { type: "in", values: ["BankTransfer"] } } },
                },
            ],
        });
        // When a tab is activated, apply its querySnapshot filters to the shared query state.
        // flush: "sync" lets useTableQueryUrlSync's URL-driven filter overrides (which run
        // immediately after setView) land after the preset filters, not before.
        watch(() => tableQuery.state.activeViewId, (id) => {
            const view = tableViews.views.value.find((v) => v.id === id);
            const nextFilters = view?.querySnapshot?.filters;
            if (nextFilters) {
                // Deep-clone so the live query never shares references with the view's snapshot — otherwise
                // editing a filter would mutate the saved list and silently re-persist it (OP-5747).
                tableQuery.state.filters = JSON.parse(JSON.stringify(nextFilters));
            }
            else if (Object.keys(tableQuery.state.filters).length > 0) {
                tableQuery.clearAllFilters();
            }
            if (tableQuery.state.page !== 1) {
                tableQuery.setPage(1);
            }
        }, { flush: "sync" });
        // ── URL sync (preset view, filters, search, pagination) ─────────────────────
        // Composable hydrates activeViewId from `?view=` (default: "all"), so no setView("all") here.
        useTableQueryUrlSync({
            tableQuery,
            syncView: true,
            syncSearch: true,
            searchDebounceMs: 0, // the TableShell search debounces already
            defaultViewId: "all",
            defaultPageSize: ROW_OPTIONS[0],
            filterKeys: {
                status: { paramName: "status" },
                donationType: { paramName: "type" },
                paymentMethod: { paramName: "method" },
                // Clamps hydration to a single id: the backend (and mapFiltersToSearchParams) only ever
                // honors values[0], so a `?campaignId=a,b` URL must not hydrate a 2-value pill.
                campaignId: { paramName: "campaignId", deserialize: deserializeCampaignIdFilter },
                donationSource: {
                    paramName: "donationSource",
                    ownedParams: ["withoutDonationSource"],
                    serialize: serializeDonationSourceFilter,
                    deserialize: deserializeDonationSourceFilter,
                },
            },
        });
        // Reload on query state change (filters, sort, page) — excludes activeViewId
        // so this only fires AFTER the tab-switch watcher has applied the correct filters.
        // `search` is in the snapshot only to be detected: on OPSEARCH a change of the term is served by the
        // debounced watcher below; on OPDON it filters the loaded rows and only the page reset that comes
        // with it needs a load.
        // MUST be registered AFTER useTableQueryUrlSync() above: that composable hydrates
        // URL filters synchronously during its own setup, mutating tableQuery.state.filters.
        // A watcher only reacts to mutations that happen after it starts observing, so
        // registering this watcher below the hydration means that synchronous mutation
        // doesn't trigger it — onMounted's explicit loadDonationsSafe() stays the single
        // initial request. Registering it above (the previous position) meant the
        // hydration mutation AND onMounted's explicit call each fired their own load.
        watch(() => ({
            filters: tableQuery.state.filters,
            // `filters` is the same reactive object on both sides of the callback; the key is what tells a
            // filter change apart from a search change landing in the same tick.
            filtersKey: JSON.stringify(tableQuery.state.filters),
            page: tableQuery.state.page,
            pageSize: tableQuery.state.pageSize,
            sortKey: JSON.stringify(tableQuery.state.sort),
            search: tableQuery.state.search,
        }), (next, prev) => {
            // CONSTRAINT: OPAL's useTableQuery.setSearch sets `search` AND `page = 1` in the same tick.
            // On OPSEARCH that page reset belongs to the debounced search watcher below; loading here as well
            // fires an undebounced request with a one-character term and races the debounced one (R97).
            // On OPDON no `q` is sent, so the page reset is the only reason to load and it loads here.
            // Only a change confined to the term (plus that page reset) is skipped: a filter or page-size
            // change landing in the same tick still loads.
            const onlySearchChanged = next.search !== prev.search &&
                next.pageSize === prev.pageSize &&
                next.sortKey === prev.sortKey &&
                next.filtersKey === prev.filtersKey;
            if (onlySearchChanged && (store.searchedByServer || next.page === prev.page))
                return;
            void loadDonationsSafe();
        }, { deep: true });
        // Search term → server round-trip, debounced like the URL sync (300 ms). The page is whatever the
        // state says: OPAL's setSearch already reset it for a typed term, and a URL change (browser back
        // onto ?q=…&page=2) brings a term and a page that belong together.
        watchDebounced(() => tableQuery.state.search, () => {
            if (!store.searchedByServer)
                return; // OPDON: `filteredDonations` serves the term in memory
            void loadDonationsSafe();
        }, { debounce: 300 });
        // The backend that serves the list decides where the term is evaluated. When it changes while a term
        // is set (a first page arriving from OPSEARCH, typically with a `?q=` URL), the rows on screen were
        // fetched the other way: reload them so the term is applied by the right side.
        // CONSTRAINT: at most one such reload per term. During a rolling restart the reload itself can come
        // back from the other backend; reloading on every flip makes the list ping-pong without end.
        let backendReloadDoneForTerm = false;
        watch(() => tableQuery.state.search, () => {
            backendReloadDoneForTerm = false;
        });
        watch(() => store.searchedByServer, () => {
            if (!tableQuery.state.search?.trim() || backendReloadDoneForTerm)
                return;
            backendReloadDoneForTerm = true;
            void loadDonationsSafe();
        });
        // ── "Filtrer" dropdown + clickable filter pills ───────────────────────────────
        // Backs both the Level-2 "Filtrer" trigger and the Level-3 pills: TableShell emits
        // @edit-filter(key) when a pill is clicked, and we call openForField(key) to open the
        // selector on that field (the ref is bound in the #filter slot). All fields are 'list'
        // type (status/donationType/paymentMethod/donationSource are multi-select; campaignId
        // is single-select, see below) — selecting values applies an "in" filter via @apply.
        const filterSelectorRef = ref(null);
        const filterFields = computed(() => [
            {
                key: "status",
                label: t("finance.donations.columns.status"),
                icon: "ri-pulse-line",
                type: "list",
                items: statusItems.value,
                selectAllLabel: t("table.selectAll"),
                step2SearchPlaceholder: t("finance.donations.filter.searchValue"),
            },
            {
                key: "donationType",
                label: t("finance.donations.columns.type"),
                icon: "ri-repeat-line",
                type: "list",
                items: donationTypeItems.value,
                selectAllLabel: t("table.selectAll"),
                step2SearchPlaceholder: t("finance.donations.filter.searchValue"),
            },
            {
                key: "paymentMethod",
                label: t("finance.donations.columns.paymentMethod"),
                icon: "ri-bank-card-line",
                type: "list",
                items: paymentMethodItems.value,
                selectAllLabel: t("table.selectAll"),
                step2SearchPlaceholder: t("finance.donations.filter.searchValue"),
            },
            {
                key: "donationSource",
                label: t("finance.donations.filter.donationSource"),
                icon: "ri-broadcast-line",
                type: "list",
                items: donationSourceItems.value,
                selectAllLabel: t("table.selectAll"),
                step2SearchPlaceholder: t("finance.donations.filter.searchValue"),
            },
            {
                key: "campaignId",
                label: t("finance.donations.columns.campaign"),
                icon: "ri-flag-line",
                type: "list",
                // The backend filters on a single campaign — single-select mode keeps the UI
                // from offering more picks than the wire can actually carry.
                single: true,
                items: campaignItems.value,
                // Select-all is moot while `single` is set, but kept so the row still reads
                // correctly if `single` is ever dropped — matches the sibling fields above.
                selectAllLabel: t("table.selectAll"),
                step2SearchPlaceholder: t("finance.donations.filter.searchValue"),
            },
        ]);
        // 'list' fields emit @apply(key, values); convert to an "in" filter (or clear when empty).
        function handleFilterApply(key, values) {
            // The backend filters on ONE campaign; keep the most recent pick if the list UI returns several.
            const next = key === "campaignId" && values.length > 1 ? [values[values.length - 1]] : values;
            if (next.length > 0) {
                tableQuery.setFilter(key, { type: "in", values: next });
            }
            else {
                tableQuery.removeFilter(key);
            }
        }
        // ── Custom prefiltered lists ("Mes listes") persistence (OP-5747) ─────────────
        // A list stores the filter state it applies. It can be built by combining selected Eudonet
        // presets + existing lists ("Créer la liste"), or by saving the current filters ("Enregistrer").
        // Lists become tabs, can be renamed / deleted, and are persisted to OPREF (restored next session).
        const { savedViews, createListFromSelection, saveCurrentView, renameList, deleteList, restore: restoreViewPreferences, } = useDonationViewPreferences({
            tableQuery,
            tableViews,
            hasUrlView: () => hasUrlListSelection(route.query),
            valueLabel: (key, value) => {
                if (key === "campaignId") {
                    return campaignItems.value.find((i) => i.id === value)?.label ?? value;
                }
                return undefined;
            },
        });
        // "Enregistrer" (save-view) — persist the current filter state as a new list.
        function onSaveCurrentView() {
            saveCurrentView(tableQuery.state.filters);
        }
        // ── Rename-list modal ─────────────────────────────────────────────────────────
        const renameModalOpen = ref(false);
        const renameTargetId = ref(null);
        const renameDraft = ref("");
        function openRenameList(id, currentLabel) {
            renameTargetId.value = id;
            renameDraft.value = currentLabel;
            renameModalOpen.value = true;
        }
        function confirmRenameList() {
            if (renameTargetId.value && renameDraft.value.trim()) {
                renameList(renameTargetId.value, renameDraft.value);
            }
            renameModalOpen.value = false;
        }
        // ── Create-modal URL sync (?action=new) ──────────────────────────────────────
        watch(showCreateModal, (open) => {
            const currentAction = route.query.action;
            if (open && currentAction !== "new") {
                router
                    .replace({ query: { ...route.query, action: "new" } })
                    .catch(handleNavigationFailure("DonationListView:modal-open"));
            }
            else if (!open && currentAction === "new") {
                const { action: _action, ...rest } = route.query;
                router.replace({ query: rest }).catch(handleNavigationFailure("DonationListView:modal-close"));
            }
        });
        watch(() => route.query.action, (action) => {
            showCreateModal.value = action === "new";
        });
        // ── tabDropdowns ─────────────────────────────────────────────────────────────
        // Eudonet group = preset filter criteria (Validé, Carte bancaire, …). The "Mes listes"
        // group lists the user's created lists (each with a rename / delete ⋯ menu). Checking any
        // combination across BOTH groups and clicking "Créer la liste" creates one new list whose
        // filter merges the selected presets + the selected lists' filters.
        const tabDropdowns = computed(() => {
            const eudonetItems = DONATION_VIEW_PRESETS.map((p) => ({
                id: p.id,
                label: t(p.labelKey),
                group: "eudonet",
            }));
            const myItems = savedViews.value.map((v) => ({
                id: v.id,
                label: v.label,
                group: "my",
                moreActions: [
                    {
                        label: t("finance.donations.views.renameList"),
                        icon: "ri-pencil-line",
                        onClick: () => openRenameList(v.id, v.label),
                    },
                    {
                        label: t("finance.donations.views.deleteList"),
                        icon: "ri-delete-bin-line",
                        variant: "danger",
                        onClick: () => deleteList(v.id),
                    },
                ],
            }));
            return [
                {
                    label: t("table.tabs.myPreFilteredLists"),
                    iconLeft: "ri-equalizer-line",
                    showHeader: true,
                    selectedCountSuffix: t("table.tabs.selected"),
                    showSearch: true,
                    searchPlaceholder: t("table.tabs.searchPlaceholder"),
                    searchClearLabel: t("common.clearSearch"),
                    size: "l",
                    emptyLabel: t("table.tabs.noListCreated"),
                    groupButtons: [
                        { value: "eudonet", label: `Eudonet (${eudonetItems.length})` },
                        { value: "my", label: `${t("table.tabs.myLists")} (${myItems.length})` },
                    ],
                    items: [...eudonetItems, ...myItems],
                    footerButtons: [
                        {
                            label: t("table.tabs.createList"),
                            variant: "primary",
                            onClick: (selectedItems) => {
                                createListFromSelection(selectedItems.map((i) => i.id));
                            },
                        },
                    ],
                },
            ];
        });
        const tablePermissions = {
            canShareView: false,
        };
        // ── TableShell i18n labels ────────────────────────────────────────────────────
        const tableLabels = computed(() => buildTableLabels(t));
        // ── Column settings (server-persisted via useListSettings) ────────────────────
        // Visibility, order, pinning AND per-column widths persist to OPREF (PUT
        // /list-settings/donations) and restore next session — same pattern as the
        // contact/organisation lists. TableShell hosts the FacetedSearchDrawer from this
        // object (gear → @settings-click) and wires handleSave/handleReset internally.
        const columnSettings = useListSettings({
            entity: "donations",
            columns: () => [
                { key: "donorName", label: t("finance.donations.columns.donor"), visible: true, pinned: false },
                { key: "status", label: t("finance.donations.columns.status"), visible: true, pinned: false },
                { key: "amount", label: t("finance.donations.columns.amount"), visible: true, pinned: false },
                { key: "donationType", label: t("finance.donations.columns.type"), visible: true, pinned: false },
                { key: "paymentMethod", label: t("finance.donations.columns.paymentMethod"), visible: true, pinned: false },
                { key: "campaignName", label: t("finance.donations.columns.campaign"), visible: true, pinned: false },
                { key: "donationDate", label: t("finance.donations.columns.date"), visible: true, pinned: false },
            ],
            defaultOrder: [
                "donorName",
                "status",
                "amount",
                "donationType",
                "paymentMethod",
                "campaignName",
                "donationDate",
            ],
            defaultPinning: { left: [], right: [] },
        });
        // The leading bulk-select checkbox column is fixed (not user-managed, absent from
        // the settings drawer). TanStack appends any column missing from `columnOrder` to
        // the END, so it must be prepended explicitly to keep the checkbox first.
        const columnOrder = computed(() => ["select", ...columnSettings.order.value]);
        const columnPinning = computed(() => ({
            left: columnSettings.pinning.value.left ?? [],
            right: columnSettings.pinning.value.right ?? [],
        }));
        // ── Row selection + float action bar ─────────────────────────────────────────
        const { selectColumn, effectiveCount, allPagesSelected, totalAcrossAllPages, clearSelection, onSelectAllPages } = useListRowSelection({
            t,
            rows: () => filteredDonations.value,
            getRowId: (donation) => donation.id,
            totalCount: () => pagerTotalItems.value,
        });
        // ── Table columns ─────────────────────────────────────────────────────────────
        // CONSTRAINT: the sort and its page reset land in the same tick; split them (an await between) and the
        // reload watcher fires twice, once for the old page.
        function applySort(column, direction) {
            tableQuery.setSort(column, direction);
            if (tableQuery.state.page !== 1)
                tableQuery.setPage(1);
        }
        const sortMenu = (column) => [
            { label: t("table.sortAZ"), onClick: () => applySort(column, "asc") },
            { label: t("table.sortZA"), onClick: () => applySort(column, "desc") },
        ];
        const donationColumns = [
            {
                key: "donorName",
                header: t("finance.donations.columns.donor"),
                cell: ({ row }) => {
                    const name = row.getValue("donorName");
                    const id = row.original.id;
                    return h("div", { class: "flex items-center gap-2 min-w-0" }, [
                        h(Avatar, { name, class: "shrink-0", presentational: true }),
                        h(RouterLink, {
                            to: `/donations/${id}`,
                            class: "text-blue-500 hover:underline",
                            "data-testid": `donation-row-link-${id}`,
                        }, () => name),
                    ]);
                },
                draggable: true,
            },
            {
                key: "status",
                header: t("finance.donations.columns.status"),
                cell: ({ row }) => {
                    const status = row.getValue("status");
                    return h(Badge, {
                        label: t(`finance.donations.status.${status}`),
                        theme: donationStatusTheme[status],
                        showCloseIcon: false,
                        size: "sm",
                        leftIcon: donationStatusIcon[status],
                    });
                },
                draggable: true,
            },
            {
                key: "amount",
                header: t("finance.donations.columns.amount"),
                cell: ({ row }) => formatAmount(row.getValue("amount"), row.original.currency),
                draggable: true,
                sortable: true,
                headerMenuItems: sortMenu("amount"),
            },
            {
                key: "donationType",
                header: t("finance.donations.columns.type"),
                cell: ({ row }) => {
                    const type = row.getValue("donationType");
                    return type ? t(`finance.donations.type.${type}`) : "–";
                },
                draggable: true,
            },
            {
                key: "paymentMethod",
                header: t("finance.donations.columns.paymentMethod"),
                cell: ({ row }) => {
                    const method = row.getValue("paymentMethod");
                    return method ? t(`finance.donations.paymentMethod.${method}`) : "–";
                },
                draggable: true,
            },
            {
                key: "campaignName",
                header: t("finance.donations.columns.campaign"),
                cell: ({ row }) => row.getValue("campaignName") ?? "–",
                draggable: true,
            },
            {
                key: "donationDate",
                header: t("finance.donations.columns.date"),
                cell: ({ row }) => formatDate(row.getValue("donationDate")),
                draggable: true,
                sortable: true,
                headerMenuItems: sortMenu("donationDate"),
            },
        ];
        const columns = computed(() => [selectColumn, ...donationColumns]);
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                    _createElementVNode("h1", _hoisted_3, _toDisplayString(_ctx.$t("finance.donations.title")), 1 /* TEXT */),
                    _createVNode(_unref(Button), {
                        label: _ctx.$t('finance.donations.new'),
                        "icon-left": "ri-add-fill",
                        "data-testid": "donation-list-create-button",
                        onClick: _cache[0] || (_cache[0] = ($event) => (showCreateModal.value = true))
                    }, null, 8 /* PROPS */, ["label"])
                ]),
                _createVNode(_unref(TableShell), {
                    query: _unref(tableQuery),
                    views: _unref(tableViews),
                    labels: tableLabels.value,
                    permissions: tablePermissions,
                    "tab-dropdowns": tabDropdowns.value,
                    "column-settings": _unref(columnSettings),
                    "show-view-selector": true,
                    "search-placeholder": _ctx.$t('finance.donations.title'),
                    onSaveView: onSaveCurrentView,
                    onEditFilter: _cache[2] || (_cache[2] = (key) => filterSelectorRef.value?.openForField(key))
                }, {
                    filter: _withCtx(() => [
                        _createVNode(_unref(TableFilterSelector), _mergeProps(filterSelectorLabels.value, {
                            ref_key: "filterSelectorRef",
                            ref: filterSelectorRef,
                            fields: filterFields.value,
                            "active-filters": _unref(tableQuery).state.filters,
                            "trigger-label": _ctx.$t('table.filter'),
                            "trigger-icon-left": "ri-filter-2-line",
                            "step1-search-placeholder": _ctx.$t('finance.donations.filter.searchField'),
                            "confirm-label": _ctx.$t('table.tabs.apply'),
                            "back-label": _ctx.$t('common.back'),
                            "advanced-filters-label": "",
                            "no-results-label": _ctx.$t('finance.donations.filter.noResults'),
                            onApply: handleFilterApply,
                            onRemoveFilter: _cache[1] || (_cache[1] = (key) => _unref(tableQuery).removeFilter(key))
                        }), null, 16 /* FULL_PROPS */, ["fields", "active-filters", "trigger-label", "step1-search-placeholder", "confirm-label", "back-label", "no-results-label"])
                    ]),
                    table: _withCtx(() => [
                        _createVNode(_unref(DataTable), {
                            columns: columns.value,
                            data: filteredDonations.value,
                            "column-visibility": _unref(columnSettings).visibility,
                            "column-order": columnOrder.value,
                            "column-pinning": columnPinning.value,
                            "column-sizing": _unref(columnSettings).widths.value,
                            loading: _unref(store).isLoading,
                            "has-settings": "",
                            "sticky-header": "",
                            "empty-text": _ctx.$t('finance.donations.empty'),
                            onSettingsClick: _unref(columnSettings).openDrawer,
                            "onUpdate:columnSizing": _unref(columnSettings).handleSizingChange
                        }, null, 8 /* PROPS */, ["columns", "data", "column-visibility", "column-order", "column-pinning", "column-sizing", "loading", "empty-text", "onSettingsClick", "onUpdate:columnSizing"])
                    ]),
                    pagination: _withCtx(() => [
                        _createVNode(_unref(Pager), {
                            "current-page": pagerCurrentPage.value,
                            "total-pages": pagerTotalPages.value,
                            "total-items": pagerTotalItems.value,
                            "items-per-page": _unref(tableQuery).state.pageSize,
                            "rows-per-page-options": ROW_OPTIONS,
                            "rows-per-page-label": _ctx.$t('pagination.rowsPerPage'),
                            "items-count-separator": _ctx.$t('pagination.of'),
                            "previous-label": _ctx.$t('pagination.previous'),
                            "next-label": _ctx.$t('pagination.next'),
                            "go-to-page-label": _ctx.$t('pagination.goToPage'),
                            "go-label": _ctx.$t('pagination.go'),
                            "show-go-to-page": "",
                            onPageChange: _unref(tableQuery).setPage,
                            onItemsPerPageChange: _unref(tableQuery).setPageSize
                        }, null, 8 /* PROPS */, ["current-page", "total-pages", "total-items", "items-per-page", "rows-per-page-label", "items-count-separator", "previous-label", "next-label", "go-to-page-label", "go-label", "onPageChange", "onItemsPerPageChange"])
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["query", "views", "labels", "tab-dropdowns", "column-settings", "search-placeholder"]),
                _createVNode(ListFloatActionBar, {
                    "selection-count": _unref(effectiveCount),
                    "all-pages-selected": _unref(allPagesSelected),
                    "total-across-all-pages": _unref(totalAcrossAllPages),
                    onDismiss: _unref(clearSelection),
                    onSelectAllPages: _unref(onSelectAllPages)
                }, null, 8 /* PROPS */, ["selection-count", "all-pages-selected", "total-across-all-pages", "onDismiss", "onSelectAllPages"]),
                (createModalMounted.value)
                    ? (_openBlock(), _createBlock(DonationCreateModal, {
                        key: 0,
                        modelValue: showCreateModal.value,
                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((showCreateModal).value = $event)),
                        onCreated: loadDonationsSafe
                    }, null, 8 /* PROPS */, ["modelValue"]))
                    : _createCommentVNode("v-if", true),
                _createVNode(_unref(Modal), {
                    modelValue: renameModalOpen.value,
                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => ((renameModalOpen).value = $event)),
                    size: "sm",
                    title: _ctx.$t('finance.donations.views.renameTitle')
                }, {
                    footer: _withCtx(() => [
                        _createVNode(_unref(Button), {
                            variant: "secondary",
                            label: _ctx.$t('common.cancel'),
                            onClick: _cache[5] || (_cache[5] = ($event) => (renameModalOpen.value = false))
                        }, null, 8 /* PROPS */, ["label"]),
                        _createVNode(_unref(Button), {
                            variant: "primary",
                            label: _ctx.$t('finance.donations.views.save'),
                            disabled: !renameDraft.value.trim(),
                            "data-testid": "donation-rename-list-confirm",
                            onClick: confirmRenameList
                        }, null, 8 /* PROPS */, ["label", "disabled"])
                    ]),
                    default: _withCtx(() => [
                        _createVNode(_unref(TextInput), {
                            modelValue: renameDraft.value,
                            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((renameDraft).value = $event)),
                            label: _ctx.$t('finance.donations.views.renameLabel'),
                            "data-testid": "donation-rename-list-input",
                            "full-width": "",
                            onKeyup: _withKeys(confirmRenameList, ["enter"])
                        }, null, 8 /* PROPS */, ["modelValue", "label"])
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["modelValue", "title"])
            ]));
        };
    }
});
